import React, { useState, useEffect } from "react";
import i18n from "i18next";
import { IKonfigurationSt } from "../../Models/IKonfigurationSt";
import { fetchWrapper } from "../../helpers/fetchwrapper";
import { baseUrl } from "../../helpers/constants";
import { ConfiguratorStResultColumns } from "./ConfiguratorStResultColumns";
import useWindowSize from "../../helpers/useWindowSize";
import { IKonfigurationStPos } from "../../Models/IKonfigurationStPos";
import { getParameterList } from "./GetParameterList";
import ConfiguratorStResultParameter from "./ConfiguratorStResultParameter";
import DocumentViewer from "../../Controls/DocumentViewer";
import LuebArticleDetailsDialog from "../../Controls/LuebArticleDetailsDialog";
import { helperMethods } from "../../helpers/helperMethods";
import { getPositionsForConfig } from "./GetPositionsForConfig";
import { getGesamtwirkungsgrad } from "./GetGesamtWirkungsgrad";
import { getUebersetzungsverhaeltnis } from "./GetUebersetzungsverhaeltnis";
import DownloadNotification from "../../Controls/DownloadDialog";
import {
  Text,
  Link,
  Label,
  MessageBar,
  Tab,
  TabList,
  Input,
  InfoLabel,
  DataGrid,
  DataGridHeader,
  DataGridRow,
  DataGridHeaderCell,
  DataGridBody,
  DataGridCell,
  Button,
  Subtitle1,
} from "@fluentui/react-components";
import {
  AddFilled,
  ArrowLeftRegular,
  ClosedCaptionRegular,
  DismissRegular,
  InfoRegular,
  MailArrowForwardRegular,
  MailCheckmarkRegular,
  SaveRegular,
  SubtractFilled,
} from "@fluentui/react-icons";
import RequestOfferDialog from "./RequestOfferDialog";
import SaveResultDialog from "./SaveResultDialog";
import { StackShim } from "@fluentui/react-migration-v8-v9";

interface IConfiguratorStResultProps {
  configurationParameter: any;
  onConfigurationFinished: any;
  finishedConfig: IKonfigurationSt | undefined;
  preiseSichtbar: boolean;
  isVertrieb: boolean;
  onDismiss(): void;
  onFinishedConfigChange(item: IKonfigurationSt): void;
  onFinishConfig(): void;
  onBackButtonClick?(): void;
}

function ConfiguratorStResult(props: IConfiguratorStResultProps) {
  const [showRequestOfferDialog, setShowRequestOfferDialog] =
    useState<boolean>(false);
  const [showSaveConfigDialog, setShowSaveConfigDialog] =
    useState<boolean>(false);
  const [documentProps, setDocumentProps] = useState({ path: "", title: "" });
  const [selectedTabValue, setSelectedTabValue] = useState<string>("ergebnis");
  const [artikelDetails, setArtikelDetails] = useState({
    artikelId: "",
    benennungDE: "",
    benennungEN: "",
  });

  const [configuration, setConfiguration] = useState<
    IKonfigurationSt | undefined
  >(undefined);
  const handleConfigurationValuesChange = (name: string, val: any) => {
    setConfiguration((prevState: any) => ({
      ...prevState,
      [name]: val,
    }));
  };

  useEffect(() => {
    if (props.finishedConfig) {
      setConfiguration(props.finishedConfig);
    }
  }, [props.finishedConfig]);

  useEffect(() => {
    if (
      props.configurationParameter?.lsp3Selection?.items?.selectedLsp3Article
        ?.artikelId
    ) {
      // Konfiguration nur "komipilieren" wenn ein Artikel ausgewählt wurde, ansonsten wird diese Komponente vermutlich mit einer fertigen Konfig geöffnet
      CreateConfiguration();
    }
  }, [props.configurationParameter]);

  useEffect(() => {
    getGesamtPreis();

    if (configuration?.positionen) {
      const wirkungsgrad = getGesamtwirkungsgrad(configuration?.positionen);
      if (wirkungsgrad) {
        setGesamtWirkungsgrad(
          wirkungsgrad.toLocaleString(i18n.language, {
            style: "decimal",
            maximumFractionDigits: 3,
            minimumFractionDigits: 3,
          }),
        );
      }

      const uebersetzung = getUebersetzungsverhaeltnis(
        configuration?.positionen,
      );
      if (uebersetzung) {
        setUebersetzung(
          uebersetzung.toLocaleString(i18n.language, {
            style: "decimal",
            maximumFractionDigits: 3,
            minimumFractionDigits: 3,
          }),
        );
      }
    }
  }, [configuration, configuration?.menge]);

  const [uebersetzung, setUebersetzung] = useState("");
  const [gesamtWirkungsgrad, setGesamtWirkungsgrad] = useState("");
  const [gesamtPreis, setGesamtpreis] = useState(0);

  const [loadingConfiguration, setLoadingConfiguration] = useState(false);
  const [showDownloadNotification, setShowDownloadNotification] =
    useState(false);

  const CreateConfiguration = () => {
    setLoadingConfiguration(true);

    const configuration: IKonfigurationSt = {
      kommentar: "",
      infoAnVertrieb: "",
      id: undefined,
      userObjectGuid: undefined,
      pruefDrehMoment: props.configurationParameter?.service?.pruefDrehMoment,
      laserBeschriftung:
        props.configurationParameter?.service?.laserBeschriftung,
      directionOfRotation:
        props.configurationParameter?.service?.directionOfRotation,
      mountingDirection:
        props.configurationParameter?.service?.mountingDirection,
      mountingAngle: props.configurationParameter?.service?.mountingAngle,
      menge: 1,
      status: 0,
      customToolBenennung:
        props.configurationParameter?.tool?.customToolBenennung,
      trimmen: props.configurationParameter?.service?.trimmen,
      withProtectiveCoating:
        props.configurationParameter?.gearedOffsetHeadSpecifications
          ?.withProtectiveCoating,
      directionOfOutputSocketGear:
        props.configurationParameter?.outputSocketSpecifications
          ?.abtriebsrichtung,
      zusammenbau: props.configurationParameter?.service?.toolAssembly,
      axialAdapterNotFound: props.configurationParameter?.axialAdapterNotFound
        ? props.configurationParameter?.axialAdapterNotFound
        : false,
      positionen: getPositionsForConfig(props.configurationParameter),
      parameter: getParameterList(props.configurationParameter),
    };

    fetchWrapper
      .postWithAuth(
        baseUrl + "/api/configurator/CompleteKonfiguration",
        configuration,
      )
      .then((result: any) => {
        setConfiguration(result);

        props.onConfigurationFinished(result);
      })
      .finally(() => {
        setLoadingConfiguration(false);
      });
  };

  const showPdfViewer = (artikelId: string) => {
    fetchWrapper
      .getWithAuth(
        `${baseUrl}/api/documents/GetDocumentForArtikel?artikelId=${artikelId}`,
      )
      .then((data) => {
        if (typeof data === "object" && data.hasOwnProperty("message")) {
          if (data.message == "NO_DOCUMENT_FOUND") {
          }
        } else {
          if (
            data.docFileExtension.toLowerCase().includes("pdf") ||
            data.docFileExtension.toLowerCase().includes("stl")
          ) {
            setDocumentProps({
              path: data.docPath.replace(
                "~",
                baseUrl + "/api/documents/GetDocumentFromPath?path=",
              ),
              title: data.docFileName,
            });
          }
        }
      })
      .catch((error) => {
        if (typeof error === "object" && error.hasOwnProperty("message")) {
          if (error.message == "NO_DOCUMENT_FOUND") {
          }
        }
      });
  };

  const getGesamtPreis = () => {
    //setGesamtpreis
    if (
      configuration?.positionen != undefined &&
      configuration?.positionen?.length > 0
    ) {
      let preis = 0;

      for (let i = 0; i < configuration?.positionen.length; i++) {
        if (!configuration?.positionen[i].beistellung) {
          if (configuration?.positionen[i].artikel) {
            const netto = configuration?.positionen[i].artikel?.netto;
            if (netto != undefined) {
              preis += netto;
            }
          }
        }
      }

      setGesamtpreis(preis * configuration?.menge);
    }
  };

  //  "Gear transmission ratio"

  const getGewichtProSystem = () => {
    let gewicht = 0;
    configuration?.positionen?.forEach((pos: IKonfigurationStPos) => {
      if (
        pos.artikel &&
        pos.artikel?.sachgruppeGuid != "4694257c-9ebf-4628-a50d-b0ab8c38c23f"
      ) {
        if (pos.artikel?.gewicht != null) {
          gewicht += pos.artikel?.gewicht;
        }
      }
    });
    return gewicht.toLocaleString();
  };

  const columns = ConfiguratorStResultColumns(props.preiseSichtbar);
  useEffect(() => {
    if (columns.przArtikelId) {
      showPdfViewer(columns.przArtikelId);
    } else {
      setDocumentProps({ title: "", path: "" });
    }
  }, [columns.przArtikelId]);

  useEffect(() => {
    if (columns?.clickedArtikel) {
      setArtikelDetails(columns?.clickedArtikel?.artikel);
    }
  }, [columns.clickedArtikel]);

  useEffect(() => {
    if (columns.kvmArtikelId) {
      downloadKvm(columns.kvmArtikelId);
    }
  }, [columns.kvmArtikelId]);

  const downloadKvm = (artikelId: string) => {
    fetchWrapper
      .getWithAuth(
        `${baseUrl}/api/documents/GetDocumentForArtikel?artikelId=${artikelId}&docKey=KVM`,
      )
      .then((data) => {
        setShowDownloadNotification(true);
        helperMethods.downloadFile(data, true).finally(() => {
          setShowDownloadNotification(false);
        });
        columns.resetKvmArtikelId();
      });
  };

  return (
    <>
      {showDownloadNotification && <DownloadNotification />}
      <RequestOfferDialog
        onDismiss={() => setShowRequestOfferDialog(false)}
        isDialogOpen={showRequestOfferDialog}
        finishedConfig={props.finishedConfig}
        onOfferRequested={(result: IKonfigurationSt) => {
          props.onFinishedConfigChange(result);
          setShowRequestOfferDialog(false);
        }}
      />
      <SaveResultDialog
        onSave={(result: IKonfigurationSt) => {
          props.onFinishedConfigChange(result);
          setShowSaveConfigDialog(false);
        }}
        onDismiss={() => setShowSaveConfigDialog(false)}
        isDialogOpen={showSaveConfigDialog}
        finishedConfig={props.finishedConfig}
      />
      <DocumentViewer
        title={documentProps.title}
        path={documentProps.path}
        onDismiss={() => {
          columns.resetPrzArtikelId();
        }}
        canDownload={false}
      />

      <LuebArticleDetailsDialog
        artikelId={artikelDetails?.artikelId}
        benennungDE={artikelDetails?.benennungDE}
        benennungEN={artikelDetails?.benennungEN}
        isDialogOpen={artikelDetails.artikelId != "" ? true : false}
        onClose={() => {
          setArtikelDetails({
            artikelId: "",
            benennungDE: "",
            benennungEN: "",
          });
        }}
      />
      <Subtitle1 style={{ marginLeft: "10px" }}>
        {i18n.t<string>("lsp3Result")}
      </Subtitle1>
      <div className="lueb-ContentBlock" style={{ marginBottom: "5px" }}>
        <TabList
          style={{ marginBottom: "20px" }}
          selectedValue={selectedTabValue}
          onTabSelect={(ev, data) => {
            if (typeof data.value == "string") {
              setSelectedTabValue(data.value);
            }
          }}
        >
          <Tab value={"ergebnis"}>{i18n.t<string>("ergebnis")}</Tab>
          <Tab value={"parameter"}>{i18n.t<string>("schraubfall")}</Tab>
          {configuration &&
            configuration?.status >= 40 &&
            configuration?.infoAnVertrieb.length > 0 && (
              <Tab value={"customer"}>{i18n.t<string>("customer")}</Tab>
            )}
        </TabList>
        {selectedTabValue == "ergebnis" && (
          <>
            <DataGrid
              columns={columns.columns}
              items={configuration ? configuration.positionen : []}
              resizableColumns
              columnSizingOptions={{
                actionId: { defaultWidth: 45, minWidth: 45 },
                artikelId: { minWidth: 90, defaultWidth: 90 },
                benennungM1: { minWidth: 330, defaultWidth: 330 },
                sachgruppeId: { minWidth: 140, defaultWidth: 140 },
                preisId: { minWidth: 100, defaultWidth: 100 },
                bereitstellungId: { minWidth: 45, defaultWidth: 60 },
              }}
            >
              <DataGridHeader>
                <DataGridRow>
                  {({ renderHeaderCell }) => (
                    <DataGridHeaderCell>
                      {renderHeaderCell()}
                    </DataGridHeaderCell>
                  )}
                </DataGridRow>
              </DataGridHeader>
              <DataGridBody<IKonfigurationStPos>>
                {({ item, rowId }) => (
                  <>
                    <DataGridRow<IKonfigurationStPos> key={rowId}>
                      {({ renderCell }) => (
                        <DataGridCell>{renderCell(item)}</DataGridCell>
                      )}
                    </DataGridRow>
                    {props.preiseSichtbar &&
                      configuration &&
                      rowId == configuration?.positionen.length - 1 && (
                        <DataGridRow<IKonfigurationStPos>>
                          {({ renderCell }) => (
                            <DataGridCell>
                              {renderCell({
                                artikel: { netto: gesamtPreis },
                                guid: undefined,
                                position: 0,
                                artikelId: i18n.t<string>("gesamtPreis"),
                                menge: 0,
                                beistellung: false,
                              })}
                            </DataGridCell>
                          )}
                        </DataGridRow>
                      )}
                    {item.beistellung && (
                      <MessageBar intent="info">
                        {i18n.t<string>("toolProvision")}
                      </MessageBar>
                    )}
                  </>
                )}
              </DataGridBody>
            </DataGrid>

            {configuration?.axialAdapterNotFound && (
              <MessageBar intent="warning">
                {i18n.t<string>("axialAdapterNotFound")}
              </MessageBar>
            )}
            {configuration?.userObjectGuid == undefined && (
              <StackShim>
                <Label style={{ marginTop: "8px", fontWeight: "bolder" }}>
                  {i18n.t<string>("menge")}
                </Label>
                <div
                  style={{
                    display: "flex",
                    flexDirection: "row",
                    alignItems: "center",
                    marginBottom: "5px",
                  }}
                >
                  <Button
                    icon={<SubtractFilled color="#ff6600" />}
                    appearance="transparent"
                    onClick={() => {
                      if (configuration) {
                        if (configuration?.menge > 1) {
                          handleConfigurationValuesChange(
                            "menge",
                            configuration?.menge - 1,
                          );
                        }
                      }
                    }}
                  />
                  <Input
                    style={{ width: "65px" }}
                    inputMode="numeric"
                    defaultValue={
                      configuration?.menge
                        ? configuration.menge.toString()
                        : "0"
                    }
                    value={
                      configuration?.menge
                        ? configuration.menge.toString()
                        : "0"
                    }
                    type="number"
                    onChange={(ev, data) => {
                      handleConfigurationValuesChange(
                        "menge",
                        Number(data.value),
                      );
                    }}
                  />

                  <Button
                    icon={<AddFilled color="#ff6600" />}
                    appearance="transparent"
                    onClick={() => {
                      if (configuration) {
                        handleConfigurationValuesChange(
                          "menge",
                          configuration?.menge + 1,
                        );
                      }
                    }}
                  />
                </div>
              </StackShim>
            )}

            {configuration?.userObjectGuid != undefined && (
              <>
                <Label style={{ marginTop: "8px", fontWeight: "bolder" }}>
                  {i18n.t<string>("menge")}
                </Label>
                <div style={{ marginBottom: "5px" }}>
                  {configuration?.menge}
                </div>
              </>
            )}
            <InfoLabel
              style={{ marginTop: "8px", fontWeight: "bolder" }}
              info={i18n.t<string>("gewichtProSystemHint")}
            >
              {i18n.t<string>("gewichtProSystem")}
            </InfoLabel>
            <div>{getGewichtProSystem()}</div>
            <Label style={{ marginTop: "8px", fontWeight: "bolder" }}>
              {i18n.t<string>("outputTorque")}
            </Label>
            <div>
              {configuration?.pruefDrehMoment.toLocaleString(undefined, {
                maximumFractionDigits: 3,
              }) +
                " / " +
                (configuration?.pruefDrehMoment
                  ? (
                      configuration?.pruefDrehMoment * 8.8507457676
                    ).toLocaleString(undefined, {
                      maximumFractionDigits: 3,
                    })
                  : "")}
            </div>
            <Label style={{ marginTop: "8px", fontWeight: "bolder" }}>
              {i18n.t<string>("gesamtUebersetzung")}
            </Label>
            <div>{uebersetzung}</div>
            <Label style={{ marginTop: "8px", fontWeight: "bolder" }}>
              {i18n.t<string>("gesamtWirkungsgrad")}
            </Label>
            <div>{gesamtWirkungsgrad}</div>
            {[
              "Standard_FL_G",
              "Inline_FL_G",
              "Standard_FL_O",
              "Inline_FL_O",
            ].includes(props.configurationParameter.gearbox.key) && (
              <>
                <Label style={{ marginTop: "8px", fontWeight: "bolder" }}>
                  {i18n.t<string>("withProtectiveCoating")}
                </Label>
                <div>
                  {configuration?.withProtectiveCoating
                    ? i18n.t<string>("yes")
                    : i18n.t<string>("no")}
                </div>
              </>
            )}
            {configuration?.directionOfOutputSocketGear && (
              <div>
                <Label style={{ marginTop: "8px", fontWeight: "bolder" }}>
                  {i18n.t<string>("abtriebsrichtung")}
                </Label>
                <div>
                  {i18n.t<string>(configuration?.directionOfOutputSocketGear)}
                </div>
              </div>
            )}
            <Label style={{ marginTop: "8px", fontWeight: "bolder" }}>
              {i18n.t<string>("toolAssembly")}
            </Label>
            <div>
              {configuration?.zusammenbau
                ? i18n.t<string>("yes")
                : i18n.t<string>("no")}
            </div>

            {configuration?.customToolBenennung && (
              <div>
                <Label style={{ marginTop: "8px", fontWeight: "bolder" }}>
                  {i18n.t<string>("customToolBenennung")}
                </Label>
                <div>{configuration?.customToolBenennung}</div>
              </div>
            )}

            {configuration?.mountingDirection && (
              <>
                <Label style={{ marginTop: "8px", fontWeight: "bolder" }}>
                  {i18n.t<string>("mountingAngle")}
                </Label>
                <div>
                  {i18n.t<string>(configuration?.mountingDirection)}
                  {configuration.mountingAngle != 0
                    ? configuration.mountingAngle + " °"
                    : ""}
                </div>
              </>
            )}

            {["Standard_FL_O", "Inline_FL_O"].includes(
              props.configurationParameter.gearbox.key,
            ) && (
              <>
                <Label style={{ marginTop: "8px", fontWeight: "bolder" }}>
                  {i18n.t<string>("trimmen")}
                </Label>
                <div>
                  {configuration?.trimmen
                    ? i18n.t<string>("yes")
                    : i18n.t<string>("no")}
                </div>
              </>
            )}
            {configuration?.laserBeschriftung && (
              <>
                <Label style={{ marginTop: "8px", fontWeight: "bolder" }}>
                  {i18n.t<string>("sonderbeschriftung")}
                </Label>
                <div>{configuration?.laserBeschriftung}</div>
              </>
            )}

            {props.preiseSichtbar && (
              <Text
                style={{
                  display: "flex",
                  justifyContent: "flex-end",
                }}
              >
                <Text style={{ color: "#ff6600", fontWeight: 700 }}>*</Text>
                {i18n.t<string>("priceNote")}
              </Text>
            )}
          </>
        )}
        {selectedTabValue == "parameter" && (
          <ConfiguratorStResultParameter
            configurationParameter={
              configuration?.parameter ? configuration?.parameter : []
            }
          />
        )}
        {selectedTabValue == "customer" && (
          <>
            <Label weight="semibold">{i18n.t<string>("customer")}</Label>
            <StackShim>
              <Text>{configuration?.gesendetVon?.fullName}</Text>
              <Text>{configuration?.gesendetVon?.firma}</Text>
              <Link href={"mailto:" + configuration?.gesendetVon?.email}>
                {configuration?.gesendetVon?.email}
              </Link>
            </StackShim>
            <Label weight="semibold" style={{ marginTop: 10 }}>
              {i18n.t<string>("infoAnVertrieb")}
            </Label>
            <StackShim style={{ marginBottom: "10px" }}>
              {configuration?.infoAnVertrieb.split("\n").map((text) => {
                return <Text>{text}</Text>;
              })}
            </StackShim>
          </>
        )}
        <div
          style={{
            display: "flex",
            justifyContent:
              props.finishedConfig && props.finishedConfig.status > 40
                ? "flex-end"
                : "space-between",
            marginTop: "10px",
          }}
        >
          <div>
            {" "}
            {props.finishedConfig?.status == 0 && (
              <Button
                icon={<ArrowLeftRegular />}
                onClick={() => {
                  if (props.onBackButtonClick) {
                    props.onBackButtonClick();
                  }
                }}
              >
                {i18n.t<string>("back")}
              </Button>
            )}
          </div>

          <div>
            <Button
              icon={/* <ClosedCaptionRegular /> */ <DismissRegular />}
              style={{ marginRight: "5px" }}
              onClick={() => {
                props.onDismiss();
              }}
            >
              {props.finishedConfig?.status == 0
                ? i18n.t<string>("close")
                : i18n.t<string>("close")}
            </Button>
            {!props.finishedConfig?.userObjectGuid && (
              <Button
                appearance="primary"
                icon={<SaveRegular />}
                onClick={() => {
                  setShowSaveConfigDialog(true);
                }}
              >
                {i18n.t<string>("saveConfig")}
              </Button>
            )}

            {props.finishedConfig?.userObjectGuid &&
              props.finishedConfig?.status == 20 && (
                <Button
                  appearance="primary"
                  icon={<MailArrowForwardRegular />}
                  onClick={() => {
                    setShowRequestOfferDialog(true);
                  }}
                >
                  {i18n.t<string>("requestOffer")}
                </Button>
              )}

            {props.finishedConfig?.userObjectGuid &&
              props.finishedConfig?.status == 40 &&
              props.isVertrieb && (
                <Button
                  appearance="primary"
                  icon={<MailCheckmarkRegular />}
                  onClick={() => {
                    props.onFinishConfig();
                  }}
                >
                  {i18n.t<string>("offerSent")}
                </Button>
              )}
          </div>
        </div>
      </div>
    </>
  );
}

export default ConfiguratorStResult;
